import { useMemo, useCallback } from 'react';
import { usePollingFileStatusMutation } from 'src/store/services';
import { useSession } from '../useSession';
import { BatchContentResponse, Format, BatchFile } from 'src/types';
import { COMMON_ERROR_TEXT } from 'src/constants';

const POLLING_TIMEOUT = 25;

export const usePollingFileStatus = () => {
  const {
    appUser: { user_id },
  } = useSession();

  const [
    pollingFileStatusMutation,
    { isLoading: isPollingLoading, isError: isPollingError },
  ] = usePollingFileStatusMutation();

  const pollingFileStatus = useCallback(
    async ({
      file_id,
      format,
      download_filename_override,
    }: BatchFile): Promise<BatchContentResponse> => {
      const body = {
        user_id,
        files: [
          {
            file_id,
            format,
            download_filename_override,
          },
        ],
        timeout_sec: POLLING_TIMEOUT,
      };

      const MAX_ATTEMPTS = format === Format.PODCAST ? 9 : 2;
      let result: BatchContentResponse | undefined;

      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        result = await pollingFileStatusMutation(body).unwrap();

        if (result.in_progress.length === 0) {
          return result;
        }
      }

      if (!result) {
        throw new Error(COMMON_ERROR_TEXT);
      }

      return result;
    },
    [user_id, pollingFileStatusMutation],
  );

  return useMemo(
    () => ({
      isPollingLoading,
      isPollingError,
      pollingFileStatus,
    }),
    [isPollingLoading, isPollingError, pollingFileStatus],
  );
};
